import { makeStyles } from '@material-ui/core';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Badge from '@material-ui/core/Badge';
import styles from './styles';
import { css } from 'styled-components';
import { Toolbar } from 'common/ui/Toolbar';
import { useWorklistFiltersContext } from '../../WorklistFilters/WorklistFiltersContext';
import { useCallback, useMemo } from 'react';
import { WorklistSurface, WorklistSurfaceType } from '../types';
import { WORKLIST_TABS } from 'config/constants';
import type { WorklistTab } from 'config/constants';
import { useWorklistAutoLoad } from 'hooks/useWorklistAutoLoad';
import Skeleton from '@material-ui/lab/Skeleton';

// @ts-expect-error [EN-7967] - TS2345 - Argument of type '{ readonly searchTextField: { readonly backgroundColor: "#1a2a37"; readonly borderRadius: 16; readonly marginRight: 10; }; readonly searchTextFieldInput: { readonly paddingLeft: 37; readonly paddingRight: 20; ... 5 more ...; readonly fontSize: "1.1rem"; }; ... 6 more ...; readonly badge: { ...; }; }' is not assignable to parameter of type 'Styles<Theme, {}, "badge" | "searchTextField" | "searchTextFieldInput" | "columnList" | "cancelButton" | "tabsWrapper" | "tabsIndicator" | "tabRoot" | "badgeRoot">'.
const useStyles = makeStyles(styles);

export const getSurfaceLabel = (tab: WorklistTab): string => {
  switch (tab) {
    case WorklistSurface.MY_QUEUE:
      return 'My Queue';
    case WorklistSurface.QC:
      return 'QC';
    case WorklistSurface.PENDING:
      return 'Pending';
    case WorklistSurface.IN_PROGRESS:
      return 'In Progress';
    case WorklistSurface.RECENTLY_READ:
      return 'Recently Read';
    case WorklistSurface.ARCHIVE:
      return 'Archive';
    case WorklistSurface.WORKLIST_VIEW:
      return 'Worklist View';
    default:
      return 'Pending';
  }
};

const WorklistToolbar = ({
  personalCount = 0,
  hasQualityControlPermission = false,
}: {
  personalCount: number;
  hasQualityControlPermission?: boolean;
}): React.ReactElement | null => {
  const { surface, updateSurface, worklistViews, areWorklistViewsLoading } =
    useWorklistFiltersContext();
  const { isWorklistAutoLoadEnabled, toggleWorklistAutoLoad } = useWorklistAutoLoad();
  const classes = useStyles();

  const resetWorklistAutoLoadToggle = useCallback(
    (prevSurface?: WorklistSurfaceType) => {
      if (isWorklistAutoLoadEnabled) {
        toggleWorklistAutoLoad(prevSurface);
      }
    },
    [isWorklistAutoLoadEnabled, toggleWorklistAutoLoad]
  );

  const personalTabLabel = (
    <Badge
      classes={{ root: classes.badgeRoot, badge: classes.badge }}
      color="secondary"
      badgeContent={personalCount === 100 ? '99+' : personalCount}
      data-testid="queue-count"
    >
      My Queue
    </Badge>
  );

  const availableTabs = useMemo(
    () =>
      hasQualityControlPermission
        ? WORKLIST_TABS
        : WORKLIST_TABS.filter((tab) => tab !== WorklistSurface.QC),
    [hasQualityControlPermission]
  );

  return (
    <Toolbar>
      <div className={classes.tabsWrapper}>
        <Tabs
          data-testid="worklist-tabs"
          value={surface}
          onChange={(evt, value) => {
            resetWorklistAutoLoadToggle(surface);
            updateSurface(value);
          }}
          // @ts-expect-error [EN-7967] - TS2551 - Property 'tabsRoot' does not exist on type 'ClassNameMap<"badge" | "searchTextField" | "searchTextFieldInput" | "columnList" | "cancelButton" | "tabsWrapper" | "tabsIndicator" | "tabRoot" | "badgeRoot">'. Did you mean 'tabRoot'?
          classes={{ root: classes.tabsRoot, indicator: classes.tabsIndicator }}
          TabIndicatorProps={{ children: <div /> }}
        >
          {availableTabs.map((tab) => {
            if (worklistViews.length === 0 && tab === WorklistSurface.WORKLIST_VIEW) {
              if (areWorklistViewsLoading) {
                return (
                  <Tab
                    key={tab}
                    label={<Skeleton width="75%" height="50%" />}
                    classes={{ root: classes.tabRoot }}
                    disableRipple
                    data-testid={tab}
                    data-analytics-id={`worklist-tab-${tab.toLowerCase()}`}
                    css={css`
                      app-region: no-drag;
                    `}
                    value={tab}
                  />
                );
              } else {
                return null;
              }
            }

            return (
              <Tab
                key={tab}
                label={tab === WorklistSurface.MY_QUEUE ? personalTabLabel : getSurfaceLabel(tab)}
                classes={{ root: classes.tabRoot }}
                disableRipple
                data-testid={tab}
                data-analytics-id={`worklist-tab-${tab.toLowerCase()}`}
                css={css`
                  app-region: no-drag;
                `}
                value={tab}
              />
            );
          })}
        </Tabs>
      </div>
    </Toolbar>
  );
};

export default WorklistToolbar;
